import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { dashboardApi, inventoryApi } from "../services/api";
import {
  FiShoppingCart,
  FiClock,
  FiArrowRight,
  FiShoppingBag,
  FiLayers,
} from "react-icons/fi";
import { toast } from "react-toastify";

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === "—") return "—";
  try {
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const parts = dateStr.slice(0, 10).split("-");
      return parts[2] + "-" + parts[1] + "-" + parts[0];
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return day + "-" + month + "-" + year;
  } catch {
    return String(dateStr);
  }
};

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remainingStockAmount, setRemainingStockAmount] = useState(0);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await dashboardApi.getSummary();
      setData(res.data);

      const backendAmount =
        res.data?.counts?.remaining_purchase_amount ??
        res.data?.remainingStock?.total_amount;

      if (backendAmount !== undefined && backendAmount !== null && Number(backendAmount) > 0) {
        setRemainingStockAmount(Number(backendAmount));
      } else {
        // Fallback: fetch available in-stock items and calculate sum(price * quantity)
        try {
          const invRes = await inventoryApi.getAll({ is_sale: 0 });
          const itemsList = invRes.data || [];
          const inStock = itemsList.filter(
            (it) =>
              !it.is_sale &&
              it.is_sale !== 1 &&
              it.is_sale !== "1" &&
              it.is_sale !== true &&
              !it.is_returned &&
              it.is_returned !== 1 &&
              (parseInt(it.quantity, 10) || 1) > 0
          );
          const totalVal = inStock.reduce((sum, it) => {
            const p = parseFloat(it.price) || 0;
            const q = parseInt(it.quantity, 10) || 1;
            return sum + p * q;
          }, 0);
          setRemainingStockAmount(totalVal);
        } catch (invErr) {
          console.error("Failed to calculate remaining stock valuation:", invErr);
        }
      }
    } catch (err) {
      toast.error("Failed to load dashboard metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const counts = data?.counts || {};
  const warrantyAlerts = data?.warrantyAlerts || { purchases: [], sales: [], totalExpiringOrExpired: 0 };
  const bottomLineAlerts = data?.bottomLineAlerts || { items: [], totalCount: 0 };
  const recentPurchases = data?.recentPurchases || [];
  const recentSales = data?.recentSales || [];
  const finalRemainingPurchaseAmount =
    remainingStockAmount ||
    Number(counts.remaining_purchase_amount || 0) ||
    Number(data?.remainingStock?.total_amount || 0) ||
    0;

  return (
    <div className="fade-in">
      {/* Welcome Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #064E3B 0%, #0F172A 100%)",
          borderRadius: "var(--radius-lg)",
          padding: "26px 32px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 20,
          marginBottom: 28,
          boxShadow: "var(--shadow-lg)",
          border: "1px solid rgba(16, 185, 129, 0.2)",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <h2 style={{ fontSize: "1.5rem", fontWeight: 800 }}>
              Welcome, {user?.name || user?.username}!
            </h2>
            <span
              style={{
                backgroundColor: "rgba(16, 185, 129, 0.25)",
                color: "#6EE7B7",
                padding: "3px 10px",
                borderRadius: 12,
                fontSize: "0.72rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                border: "1px solid rgba(16, 185, 129, 0.4)",
              }}
            >
              {user?.role}
            </span>
          </div>
          <p style={{ color: "var(--slate-300)", fontSize: "0.9rem" }}>
            Here is your real-time overview of current inventory stock, purchase orders, sales transactions, and warranty statuses.
          </p>
        </div>



      </div>


      {/* Warranty Expiry Alert Banner (if any) */}
      {warrantyAlerts.totalExpiringOrExpired > 0 && (
        <div
          style={{
            backgroundColor: "var(--warning-bg)",
            border: "1px solid #FCD34D",
            borderRadius: "var(--radius-md)",
            padding: "14px 20px",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "var(--warning)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiClock size={20} />
            </div>
            <div>
              <div style={{ fontWeight: 700, color: "#92400E", fontSize: "0.92rem" }}>
                Warranty Attention Required ({warrantyAlerts.totalExpiringOrExpired} {warrantyAlerts.totalExpiringOrExpired === 1 ? "item" : "items"})
              </div>
              <div style={{ color: "#B45309", fontSize: "0.82rem" }}>
                Some items have warranties that are expiring within 30 days or have expired.
              </div>
            </div>
          </div>
          <Link
            to="/warranty"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#92400E",
              fontWeight: 700,
              fontSize: "0.85rem",
              padding: "6px 14px",
              borderRadius: "var(--radius-md)",
              backgroundColor: "rgba(245, 158, 11, 0.2)",
            }}
          >
            View Warranty Tracker <FiArrowRight size={15} />
          </Link>
        </div>
      )}

      {/* Primary KPI Metrics: Total Purchase Amount, Total Sale Amount, Remaining Item Purchase Amount */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 20,
          marginBottom: 30,
        }}
      >
        {/* Total Purchase Amount */}
        <Link
          to="/purchases"
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            textDecoration: "none",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Purchase Amount
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#DCFCE7", color: "#15803D" }}>
              <FiShoppingCart size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "#15803D",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`Rs. ${Number(counts.total_purchase_amount ?? data?.purchases?.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          >
            Rs. {Number(counts.total_purchase_amount ?? data?.purchases?.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#15803D" }} />
            <span>{counts.purchase_items ?? data?.purchases?.total_quantity ?? 0} items purchased</span>
          </div>
        </Link>

        {/* Total Sale Amount */}
        <Link
          to="/sales"
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            textDecoration: "none",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Sale Amount
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#F3E8FF", color: "#7E22CE" }}>
              <FiShoppingBag size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "#7E22CE",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`Rs. ${Number(counts.total_sale_amount ?? data?.sales?.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          >
            Rs. {Number(counts.total_sale_amount ?? data?.sales?.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#7E22CE" }} />
            <span>{counts.sale_items ?? data?.sales?.total_quantity ?? 0} items sold</span>
          </div>
        </Link>

        {/* Remaining (Remaining item purchase amount) */}
        <Link
          to="/stock"
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            textDecoration: "none",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Remaining Item Purchase Amount
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#EEF2FF", color: "#4F46E5" }}>
              <FiLayers size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "#4F46E5",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`Rs. ${Number(finalRemainingPurchaseAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Purchase amount of current in-stock items)`}
          >
            Rs. {Number(finalRemainingPurchaseAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#4F46E5" }} />
            <span>{counts.totalStockUnits ?? counts.available_items ?? data?.remainingStock?.total_units ?? 0} in-stock items (Purchase Value)</span>
          </div>
        </Link>

      </div>

      {/* Two Column Grid for Recent Purchases & Recent Sales */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))", gap: 24 }}>
        {/* Recent Purchases Table */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--slate-200)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "var(--slate-50)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: "#DCFCE7",
                  color: "#15803D",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiShoppingCart size={16} />
              </div>
              <div>
                <h3 style={{ fontSize: "0.98rem", fontWeight: 700, color: "var(--navy)" }}>Recent Purchases</h3>
                <p style={{ fontSize: "0.74rem", color: "var(--slate-500)" }}>Latest incoming vendor acquisitions</p>
              </div>
            </div>
            <Link
              to="/purchases"
              style={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "var(--accent)",
                display: "flex",
                alignItems: "center",
                gap: 4,
                textDecoration: "none",
              }}
            >
              View All <FiArrowRight size={14} />
            </Link>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#FAFAFA", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Bill #</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Date</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Vendor</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600, textAlign: "center" }}>Qty</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Total Amount</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Created By</th>
                </tr>
              </thead>
              <tbody>
                {recentPurchases.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 36, textAlign: "center", color: "var(--slate-400)" }}>
                      No recent purchases recorded yet.
                    </td>
                  </tr>
                ) : (
                  recentPurchases.map((p) => (
                    <tr
                      key={p.id}
                      style={{ borderBottom: "1px solid var(--slate-100)", transition: "background 0.15s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td style={{ padding: "12px 14px" }}>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            backgroundColor: "#DCFCE7",
                            color: "#15803D",
                            padding: "3px 8px",
                            borderRadius: 5,
                            fontSize: "0.78rem",
                            display: "inline-block",
                          }}
                        >
                          {p.bill_number || `BILL-${p.id}`}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", color: "var(--slate-600)", whiteSpace: "nowrap" }}>
                        {formatDate(p.purchase_date)}
                      </td>
                      <td style={{ padding: "12px 14px", fontWeight: 700, color: "var(--navy)" }}>
                        {p.vendor_name || "—"}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        <span
                          style={{
                            backgroundColor: "var(--slate-100)",
                            color: "var(--slate-700)",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: "0.76rem",
                          }}
                        >
                          {p.quantity ?? 1} {p.quantity === 1 ? "unit" : "units"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 700, color: "#15803D", whiteSpace: "nowrap" }}>
                        Rs. {Number(p.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "12px 14px", color: "var(--slate-600)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 600, color: "var(--slate-800)" }}>
                          {p.created_by_name || p.created_by_username || "—"}
                        </div>
                        {p.created_by_username && p.created_by_name && (
                          <div style={{ fontSize: "0.72rem", color: "var(--slate-400)" }}>
                            @{p.created_by_username}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Sales Table */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--slate-200)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "var(--slate-50)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: "#F3E8FF",
                  color: "#7E22CE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiShoppingBag size={16} />
              </div>
              <div>
                <h3 style={{ fontSize: "0.98rem", fontWeight: 700, color: "var(--navy)" }}>Recent Sales</h3>
                <p style={{ fontSize: "0.74rem", color: "var(--slate-500)" }}>Latest outgoing client sales</p>
              </div>
            </div>
            <Link
              to="/sales"
              style={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "var(--accent)",
                display: "flex",
                alignItems: "center",
                gap: 4,
                textDecoration: "none",
              }}
            >
              View All <FiArrowRight size={14} />
            </Link>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#FAFAFA", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Invoice #</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Date</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Client</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600, textAlign: "center" }}>Qty</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Total Amount</th>
                  <th style={{ padding: "11px 14px", color: "var(--slate-600)", fontWeight: 600 }}>Created By</th>
                </tr>
              </thead>
              <tbody>
                {recentSales.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 36, textAlign: "center", color: "var(--slate-400)" }}>
                      No recent sales recorded yet.
                    </td>
                  </tr>
                ) : (
                  recentSales.map((s) => (
                    <tr
                      key={s.id}
                      style={{ borderBottom: "1px solid var(--slate-100)", transition: "background 0.15s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td style={{ padding: "12px 14px" }}>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            backgroundColor: "#F3E8FF",
                            color: "#7E22CE",
                            padding: "3px 8px",
                            borderRadius: 5,
                            fontSize: "0.78rem",
                            display: "inline-block",
                          }}
                        >
                          {s.invoice_number || `INV-${s.id}`}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", color: "var(--slate-600)", whiteSpace: "nowrap" }}>
                        {formatDate(s.sale_date)}
                      </td>
                      <td style={{ padding: "12px 14px", fontWeight: 700, color: "var(--navy)" }}>
                        {s.client_name || "—"}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        <span
                          style={{
                            backgroundColor: "var(--slate-100)",
                            color: "var(--slate-700)",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: "0.76rem",
                          }}
                        >
                          {s.quantity ?? 1} {s.quantity === 1 ? "unit" : "units"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 700, color: "#7E22CE", whiteSpace: "nowrap" }}>
                        Rs. {Number(s.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "12px 14px", color: "var(--slate-600)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 600, color: "var(--slate-800)" }}>
                          {s.created_by_name || s.created_by_username || "—"}
                        </div>
                        {s.created_by_username && s.created_by_name && (
                          <div style={{ fontSize: "0.72rem", color: "var(--slate-400)" }}>
                            @{s.created_by_username}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
